/**
 * Firebase Cloud Messaging Service (Frontend)
 * Handles FCM token management and notification handling for web
 */

import { initializeApp, getApps, FirebaseApp } from 'firebase/app';
import { getMessaging, getToken, onMessage, Messaging, isSupported, MessagePayload } from 'firebase/messaging';
import pushTokenService from './pushToken.service';

// Firebase configuration - these should be set via environment variables
// You'll need to add these from your Firebase project settings
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY || '',
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || '',
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || '',
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || '',
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || '',
  appId: process.env.REACT_APP_FIREBASE_APP_ID || '',
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID || ''
};

let app: FirebaseApp | null = null;
let messaging: Messaging | null = null;
let isInitialized = false;

/**
 * Initialize Firebase app
 */
const initializeFirebase = (): FirebaseApp | null => {
  if (app) {
    return app;
  }

  // Check if Firebase config is available
  if (!firebaseConfig.apiKey || !firebaseConfig.projectId) {
    console.warn('⚠️ Firebase configuration not found. Push notifications will not work.');
    console.warn('   Set REACT_APP_FIREBASE_API_KEY, REACT_APP_FIREBASE_PROJECT_ID, etc. in .env');
    return null;
  }

  try {
    const existingApps = getApps();
    if (existingApps.length > 0) {
      app = existingApps[0];
    } else {
      app = initializeApp(firebaseConfig);
    }
    return app;
  } catch (error) {
    console.error('❌ Error initializing Firebase:', error);
    return null;
  }
};

/**
 * Initialize Firebase Cloud Messaging
 */
const initializeMessaging = async (): Promise<Messaging | null> => {
  if (messaging) {
    return messaging;
  }

  // Check if browser supports FCM
  const supported = await isSupported();
  if (!supported) {
    console.warn('⚠️ This browser does not support Firebase Cloud Messaging');
    return null;
  }

  const firebaseApp = initializeFirebase();
  if (!firebaseApp) {
    return null;
  }

  try {
    messaging = getMessaging(firebaseApp);
    isInitialized = true;
    console.log('✅ Firebase Messaging initialized');
    return messaging;
  } catch (error) {
    console.error('❌ Error initializing Firebase Messaging:', error);
    return null;
  }
};

/**
 * Request notification permission from the user
 */
const requestPermission = async (): Promise<NotificationPermission> => {
  if (!('Notification' in window)) {
    console.warn('⚠️ This browser does not support notifications');
    return 'denied';
  }

  if (Notification.permission === 'granted') {
    return 'granted';
  }

  if (Notification.permission === 'denied') {
    console.warn('⚠️ Notification permission was denied');
    return 'denied';
  }

  try {
    const permission = await Notification.requestPermission();
    return permission;
  } catch (error) {
    console.error('❌ Error requesting notification permission:', error);
    return 'denied';
  }
};

/**
 * Get FCM token for the current device
 * This token needs to be sent to the backend
 */
const getFCMToken = async (vapidKey?: string): Promise<string | null> => {
  const messagingInstance = await initializeMessaging();
  if (!messagingInstance) {
    return null;
  }

  // Check permission first
  const permission = await requestPermission();
  if (permission !== 'granted') {
    console.warn('⚠️ Notification permission not granted');
    return null;
  }

  try {
    // Ensure service worker is registered before getting token
    if ('serviceWorker' in navigator) {
      const registration = await navigator.serviceWorker.ready;
      console.log('[FCM] ✅ Service Worker ready:', registration.scope);
    }
    
    // Get token - if VAPID key is provided via env, use it
    const vapidKeyFromEnv = process.env.REACT_APP_FIREBASE_VAPID_KEY;
    const token = await getToken(messagingInstance, {
      vapidKey: vapidKeyFromEnv || vapidKey,
      serviceWorkerRegistration: 'serviceWorker' in navigator 
        ? await navigator.serviceWorker.ready 
        : undefined
    });

    if (token) {
      console.log('✅ [FCM] FCM token obtained:', token.substring(0, 20) + '...');
      console.log('✅ [FCM] Full token (for debugging):', token);
      return token;
    } else {
      console.warn('⚠️ [FCM] No FCM token available');
      return null;
    }
  } catch (error) {
    console.error('❌ Error getting FCM token:', error);
    return null;
  }
};

/**
 * Detect device type (mobile vs desktop)
 */
const detectDeviceType = (): 'ios' | 'android' | 'web' => {
  const userAgent = navigator.userAgent.toLowerCase();
  if (/iphone|ipad|ipod/.test(userAgent)) {
    return 'ios';
  } else if (/android/.test(userAgent)) {
    return 'android';
  }
  return 'web';
};

/**
 * Register FCM token with the backend
 */
const registerTokenWithBackend = async (
  token: string,
  deviceInfo?: {
    deviceType?: 'ios' | 'android' | 'web';
    deviceId?: string;
    appVersion?: string;
  }
): Promise<boolean> => {
  try {
    const detectedDeviceType = detectDeviceType();
    const deviceType = deviceInfo?.deviceType || detectedDeviceType;
    const deviceId = deviceInfo?.deviceId || getDeviceId();
    
    console.log('[FCM] 🔄 Registering token with backend:', {
      tokenPreview: token.substring(0, 30) + '...',
      deviceType: deviceType,
      deviceId: deviceId.substring(0, 30) + '...',
      appVersion: deviceInfo?.appVersion || '1.0.0',
      userAgent: navigator.userAgent.substring(0, 50),
      isMobile: /mobile|android|iphone|ipad/i.test(navigator.userAgent)
    });
    
    const result = await pushTokenService.registerToken({
      token,
      deviceType: deviceType,
      deviceId: deviceId,
      appVersion: process.env.REACT_APP_VERSION || '1.0.0'
    });
    
    console.log('✅ [FCM] Token registered with backend successfully:', {
      tokenId: result?.data?.token?.id,
      deviceType: result?.data?.token?.device_type,
      deviceId: result?.data?.token?.device_id ? result.data.token.device_id.substring(0, 30) + '...' : 'unknown',
      registeredAt: result?.data?.token?.created_at || result?.data?.token?.updated_at,
      isActive: result?.data?.token?.is_active
    });
    return true;
  } catch (error: any) {
    console.error('❌ [FCM] Error registering FCM token with backend:', {
      error: error.message,
      response: error.response?.data,
      status: error.response?.status
    });
    return false;
  }
};

/**
 * Handle foreground messages (when app is open)
 */
const onForegroundMessage = (
  callback: (payload: MessagePayload) => void
): (() => void) | null => {
  if (!messaging) {
    console.warn('📬 [FCM] onForegroundMessage: messaging not initialized');
    return null;
  }

  try {
    console.log('📬 [FCM] Setting up foreground message handler...');
    const unsubscribe = onMessage(messaging, (payload) => {
      console.log('📬 [FCM] onMessage callback triggered:', {
        hasNotification: !!payload.notification,
        hasData: !!payload.data,
        notificationTitle: payload.notification?.title,
        dataType: payload.data?.type
      });
      callback(payload);
    });
    console.log('✅ [FCM] Foreground message handler set up successfully');
    return unsubscribe;
  } catch (error) {
    console.error('❌ [FCM] Error setting up foreground message handler:', error);
    return null;
  }
};

/**
 * Get device ID (simple implementation)
 * This generates a unique ID per device/browser combination
 */
const getDeviceId = (): string => {
  // Try to get from localStorage first
  let deviceId = localStorage.getItem('device_id');
  if (!deviceId) {
    // Generate a unique device ID based on user agent, screen size, and timestamp
    // This ensures different devices (mobile vs desktop) get different IDs
    const screenWidth = typeof window !== 'undefined' && window.screen ? window.screen.width : 0;
    const screenHeight = typeof window !== 'undefined' && window.screen ? window.screen.height : 0;
    const deviceInfo = `${navigator.userAgent}-${screenWidth}x${screenHeight}-${navigator.language}-${Date.now()}`;
    // Hash it for privacy (simple hash)
    deviceId = btoa(deviceInfo).substring(0, 50);
    localStorage.setItem('device_id', deviceId);
    console.log('[FCM] Generated new device ID:', deviceId.substring(0, 20) + '...');
  }
  return deviceId;
};

export {
  initializeFirebase,
  initializeMessaging,
  requestPermission,
  getFCMToken,
  registerTokenWithBackend,
  onForegroundMessage,
  getDeviceId,
  detectDeviceType,
  isInitialized
};

